/** @format */

import React, { type FC } from "react";
import { useTranslation } from "react-i18next";
import { Icon, ListItem, i18n } from "senf-atomic-design-system";
import { AuthModal, useAuthContext, useGlobalState } from "senf-shared";
import { Dot } from "../../../assets/icons";
import { OptionsTopics } from "../../../data/OptionsTopics";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import Box from "../../atoms/box/Box";
import DropdownButton from "../../atoms/dropdownButton/DropdownButton";
import Input from "../../atoms/inputs/Input";
import Typography from "../../atoms/typography/Typography";
import Button from "../../core/button/Button";
import Dropdown from "../../core/dropdown/Dropdown";
import Seperator from "../../core/seperator/Seperator";
import ModalButton from "../../molecules/modalStack/ModalButton";
import { useModals } from "../../molecules/modalStack/ModalProvider";
import FormContacts from "../../organisms/modalContents/FormContacts";
import type { PostIdeaFormProps } from "./PostIdeaForm.types";

const TopicDropdown = ({ leadingIcon, text, items }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  return isMobile ? (
    <ModalButton
      leadingIcon={leadingIcon}
      variant="basic"
      pillShaped={false}
      className="w-full justify-start"
      trailingIcon="arrowhead-down-lg"
      text={text}
      size="lg"
      options={{
        title: t("dialogMap:add.chooseCategoryPlaceholder"),
        swipe: isMobile && true,
      }}
    >
      <div className="px-3 pb-5">
        {items.map(({ leadingIcon, text, onClick }) => {
          return (
            <ListItem
              leadingIcon={leadingIcon}
              primaryContent={text}
              classNames={{
                base: "h-12",
              }}
              onClick={onClick}
            />
          );
        })}
      </div>
    </ModalButton>
  ) : (
    <Dropdown
      full={true}
      hasArrow={false}
      trailingIcon="arrowhead-down-lg"
      leadingIcon={leadingIcon}
      pillShaped={false}
      size="lg"
      justifyContent="flex-start"
      width="max"
      text={text}
      className="text-left"
      // options={{
      //   size: isMobile ? "lg" : "md",
      //   title: t("dialogMap:add.chooseCategory"),
      //   modal: !!isMobile,
      // }}

      items={items}
    />
  );
};

const PostIdeaForm: FC<PostIdeaFormProps> = ({
  ideaMap,
  formik,
  isSubmitting,
  handleSubmit,
  editIdea,
  loading,
  isOut,
  project,
  geoDataType,
  // user,
}) => {
  const [translation] = useGlobalState().translationsState;

  const optionsTopics = OptionsTopics(
    ideaMap?.customTopics?.length && ideaMap?.customTopics,
  );
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { openModal, closeModal } = useModals();
  const { user } = useAuthContext();
  const isUserProfileComplete =
    user?.profileCompleted ||
    project?.settings?.demographicData?.collect === false;

  const handleCheckAuth = () => {
    const isUserAnonymous = user?.isAnonymous;

    const isUserIdPresent = !!user?.userId;
    const isEmailNotVerified = isUserIdPresent && !user?.emailVerified;

    if (!user) {
      openModal(<AuthModal project={project} />, {
        swipe: !!isMobile,
        size: isMobile && "fullscreen",
      });
      return;
    }

    if (isUserIdPresent && !isUserProfileComplete) {
      openModal(<AuthModal authAddDetails={true} project={project} />, {
        swipe: !!isMobile,
        size: isMobile && "fullscreen",
      });
      return;
    }

    if (isUserIdPresent && isEmailNotVerified && !isUserAnonymous) {
      openModal(<AuthModal authVerifyEmail={true} project={project} />, {
        swipe: !!isMobile,
        size: isMobile && "fullscreen",
      });
    }
  };
  const submitConditions = [
    { condition: isSubmitting, notice: t("dialogMap:add.submitting") },
    {
      condition: !isUserProfileComplete,
      notice: t("dialogMap:add.profileIncomplete"),
    },
    {
      condition: !formik?.values?.address && geoDataType === "location",
      notice: t("dialogMap:add.locationRequired"),
    },
    {
      condition: formik?.errors?.title,
      notice: t("util:required", { val: t("global:title") }),
    },
    {
      condition: formik?.errors?.body,
      notice: t("dialogMap:add.descriptionRequired"),
    },

    {
      condition: !formik?.values?.topic,
      notice: t("dialogMap:add.categoryRequired"),
    },

    {
      condition: isOut === true,
      notice: t("dialogMap:add.outOfProjectArea"),
    },
  ];

  const resultCondition = submitConditions.find((item) => item.condition);
  const disabled = !!resultCondition;
  const disabledNotice = resultCondition
    ? resultCondition.notice
    : t("dialogMap:add.unknownError");

  return (
    <Box gap="20px" flexDirection="column" paddingBottom="180px">
      <Input
        name="title"
        type="text"
        placeholder={t("dialogMap:add.enterIdeaTitlePlaceholder")}
        label={`${t("global:title")}*`}
        onChange={formik?.handleChange}
        onBlur={formik?.handleBlur}
        value={formik?.values.title}
        error={formik?.touched.title && Boolean(formik?.errors?.title)}
        note={formik?.touched.title && formik?.errors?.title}
        onClick={handleCheckAuth}
        minLength={10}
        maxLength={70}
        // maxRows={2}
        // maxLength: 70
      />
      <Input
        name="body"
        type="textarea"
        placeholder={t("dialogMap:add.enterIdeaDescriptionPlaceholder")}
        label={`${t("global:description")}*`}
        rows={5}
        maxRows={10}
        onChange={formik?.handleChange}
        onBlur={formik?.handleBlur}
        value={formik?.values.body}
        error={formik?.touched.body && Boolean(formik?.errors?.body)}
        note={formik?.touched.body && formik?.errors?.body}
        onClick={handleCheckAuth}
        minLength={ideaMap?.addIdeaOptions?.description?.minChar ?? 50}
        maxLength={ideaMap?.addIdeaOptions?.description?.maxChar ?? 800}
      />
      <Box flexDirection="column" gap="10px" marginBottom="20px">
        <Typography variant="bodySm">
          {t("dialogMap:add.chooseCategory")}*
        </Typography>

        <TopicDropdown
          leadingIcon={
            formik?.values?.topic ? (
              <Icon
                name="ideaCategory/default"
                style={{
                  color: ideaMap?.customTopicsObject[formik.values.topic].color,
                  transform: "scale(0.7)",
                }}
              />
            ) : (
              <Icon
                name="ideaCategory/all"
                style={{
                  transform: "scale(0.7)",
                }}
              />
            )
          }
          text={
            translation?.[
              ideaMap?.customTopicsObject?.[formik.values.topic]?.label
            ] ||
            ideaMap?.customTopicsObject?.[formik.values.topic]?.label ||
            t("dialogMap:add.chooseCategoryPlaceholder")
          }
          items={optionsTopics.map(({ value, label, color }) => {
            return {
              type: "leadingIcon",
              text: translation?.[label] || label,
              leadingIcon: <Dot color={color} />,
              zIndex: 99999,
              onClick: () => {
                formik?.setFieldValue("topic", value);
                if (isMobile) {
                  setTimeout(() => {
                    closeModal();
                  }, 300);
                }
              },
            };
          })}
        />
      </Box>

      {ideaMap?.addIdeaOptions?.contactFieldsAllowed && (
        <React.Fragment>
          <Box gap="4px" flexDirection="column">
            <Typography variant="bodySm">{t("global:optional")}</Typography>
            <Box gap="10px" flexDirection="column" marginTop="5px">
              <ModalButton
                width="max"
                variant="basic"
                justifyContent="flex-start"
                text={
                  formik?.values.weblinkTitle ||
                  formik?.values.weblink ||
                  t("util:add", { val: t("global:link") })
                }
                leadingIcon="link"
                trailingIcon="plus-md"
                options={{
                  title: t("util:add", { val: t("global:link") }),
                  id: "addWeblink",
                  cancelText: t("global:cancel"),
                  submitText: t("global:save"),
                  zIndex: 99999,
                }}
              >
                <Box marginInline="20px" flexDirection="column">
                  <FormContacts
                    formik={formik}
                    inputData={[
                      {
                        name: "weblinkTitle",
                        placeholder: "dialogMap:add.addWeblinkTitlePlaceholder",
                      },
                      {
                        name: "weblink",
                        placeholder: "dialogMap:add.addWeblinkPlaceholder",
                      },
                    ]}
                  />
                </Box>
              </ModalButton>

              <ModalButton
                variant="basic"
                justifyContent="flex-start"
                width="max"
                text={
                  formik?.values.contactTitle ||
                  formik?.values.contact ||
                  t("util:add", { val: t("global:emailAddress") })
                }
                leadingIcon="mail-fill"
                trailingIcon="plus-md"
                options={{
                  title: t("util:add", { val: t("global:emailAddress") }),

                  id: "addContact",
                  cancelText: t("global:cancel"),
                  submitText: t("global:save"),
                  zIndex: 99999,
                }}
              >
                <Box marginInline="20px" flexDirection="column">
                  <FormContacts
                    formik={formik}
                    inputData={[
                      {
                        name: "contactTitle",
                        placeholder: "dialogMap:add.addEmailTitlePlaceholder",
                      },
                      {
                        name: "contact",
                        placeholder: "dialogMap:add.addEmailPlaceholder",
                      },
                    ]}
                    header={t("dialogMap:add.addEmailDescription")}
                  />
                </Box>
              </ModalButton>
            </Box>
          </Box>

          <div className="-mx-5">
            <Seperator orientation="horizontal" />
          </div>
        </React.Fragment>
      )}
      <Button
        width="max"
        size="xl"
        trackId="addIdeaPage_submitClick"
        onClick={handleSubmit}
        variant={"primary"}
        text={
          editIdea
            ? t("dialogMap:add.updateIdea")
            : t("dialogMap:add.shareIdea")
        }
        loading={loading}
        disabled={disabled}
        disabledNotice={disabledNotice}
        // tooltipLabel="Submit"
        // key="Submit"
      />
    </Box>
  );
};

export default PostIdeaForm;
